<template>
  <div class="rm rm-theme">
    <!-- Početna: izbor izveštaja -->
    <template v-if="!currentScreen">
      <div class="rm-home-header">
        <span v-if="restaurantName" class="rm-muted">{{ restaurantName }}</span>
        <h1 class="rm-home-header__title">{{ $t("report_mobile.title") }}</h1>
        <button
          v-if="views.length > 1"
          type="button"
          class="rm-view-switch"
          :aria-label="$t('view')"
          @click="filterSheet = true"
        >
          {{ currentViewName }}
          <v-icon size="18" color="#9a3412">mdi-chevron-down</v-icon>
        </button>
      </div>

      <div class="rm-chip-row rm-home-periods">
        <button
          v-for="preset in quickPresets"
          :key="preset.key"
          type="button"
          class="rm-chip rm-chip--lg"
          :class="{ 'rm-chip--active': activePresetKey === preset.key }"
          :aria-pressed="String(activePresetKey === preset.key)"
          @click="applyPeriod(preset)"
        >
          {{ presetChipLabel(preset) }}
        </button>
        <button
          v-if="!activeQuickPreset"
          type="button"
          class="rm-chip rm-chip--lg rm-chip--active rm-num"
          aria-pressed="true"
          @click="periodSheet = true"
        >
          {{ periodShort }}
        </button>
        <button
          type="button"
          class="rm-chip rm-chip--lg rm-chip--icon"
          :aria-label="$t('report_mobile.choose_period')"
          @click="periodSheet = true"
        >
          <v-icon size="20" color="#1c1917">mdi-calendar-blank-outline</v-icon>
        </button>
      </div>

      <div class="rm-home-groups">
        <section v-for="group in menuGroups" :key="group.key" class="rm-home-group">
          <h2 class="rm-section-title">{{ group.title }}</h2>
          <div class="rm-card">
            <button
              v-for="item in group.items"
              :key="item.key"
              type="button"
              class="rm-menu-row"
              @click="openScreen(item.key)"
            >
              <span class="rm-menu-row__icon">
                <v-icon size="20" color="#c2410c">{{ item.icon }}</v-icon>
              </span>
              <span class="rm-menu-row__text">
                <span class="rm-menu-row__title">{{ item.title }}</span>
                <span class="rm-muted">{{ item.subtitle }}</span>
              </span>
              <v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon>
            </button>
          </div>
        </section>
      </div>
    </template>

    <!-- Pojedinačni izveštaj -->
    <template v-else>
      <div class="rm-detail-top">
        <div class="rm-detail-header">
          <button
            type="button"
            class="rm-icon-btn"
            :aria-label="$t('report_mobile.back')"
            @click="closeScreen"
          >
            <v-icon size="24" color="#1c1917">mdi-chevron-left</v-icon>
          </button>
          <div class="rm-detail-header__text">
            <h1 class="rm-detail-header__title">{{ currentScreen.title }}</h1>
            <span class="rm-muted rm-detail-header__sub">{{ headerSubtitle }}</span>
          </div>
          <button
            v-if="currentScreen.headerSearch"
            type="button"
            class="rm-icon-btn"
            :aria-label="$t('search')"
            :aria-pressed="String(searchOpen)"
            @click="toggleSearch"
          >
            <v-icon size="22" color="#1c1917">mdi-magnify</v-icon>
          </button>
          <button
            v-if="currentScreen.header === 'summary'"
            type="button"
            class="rm-icon-btn"
            :aria-label="$t('filters')"
            @click="filterSheet = true"
          >
            <v-icon size="22" color="#1c1917">mdi-tune-variant</v-icon>
          </button>
          <button
            type="button"
            class="rm-icon-btn"
            :aria-label="$t('report_mobile.share')"
            @click="actionsSheet = true"
          >
            <v-icon size="22" color="#1c1917">mdi-export-variant</v-icon>
          </button>
        </div>

        <div v-if="currentScreen.headerSearch && searchOpen" class="rm-detail-search">
          <label class="rm-search">
            <v-icon size="18" color="#5f5852">mdi-magnify</v-icon>
            <span class="rm-visually-hidden">{{ $t("search") }}</span>
            <input ref="searchInput" v-model="search" type="search" :placeholder="$t('search')" />
          </label>
        </div>

        <div v-if="!currentScreen.header" class="rm-chip-row">
          <button type="button" class="rm-chip" @click="periodSheet = true">
            <span class="rm-num">{{ periodShort }}</span>
            <v-icon size="16" color="#5f5852">mdi-chevron-down</v-icon>
          </button>
          <button
            v-if="currentScreen.companies"
            type="button"
            class="rm-chip"
            :class="{ 'rm-chip--accent': companyChipAccent }"
            @click="filterSheet = true"
          >
            {{ companiesChipLabel }}
            <v-icon size="16" :color="companyChipAccent ? '#9a3412' : '#5f5852'">
              mdi-chevron-down
            </v-icon>
          </button>
          <button
            v-if="currentScreen.shifts"
            type="button"
            class="rm-chip"
            :class="{ 'rm-chip--accent': !allShiftsSelected }"
            @click="filterSheet = true"
          >
            {{ shiftsLabel }}
            <v-icon size="16" :color="allShiftsSelected ? '#5f5852' : '#9a3412'">
              mdi-chevron-down
            </v-icon>
          </button>
          <button
            v-if="(currentScreen.companies || currentScreen.shifts) && currentViewName"
            type="button"
            class="rm-chip"
            @click="filterSheet = true"
          >
            {{ currentViewName }}
            <v-icon size="16" color="#5f5852">mdi-chevron-down</v-icon>
          </button>
        </div>
      </div>

      <template v-if="!currentScreen.legacy">
        <div v-if="!companiesReady" class="rm-screen">
          <div class="rm-empty">
            <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
          </div>
        </div>
        <component
          :is="currentScreen.component"
          v-else
          :key="`${currentScreenKey}-${reloadKey}`"
          v-bind="screenProps"
          :shifts.sync="shifts"
          @open-screen="openScreen"
        ></component>
      </template>
      <div v-else class="rm-legacy">
        <component :is="currentScreen.component" :key="`${currentScreenKey}-${reloadKey}`"></component>
      </div>
    </template>

    <report-mobile-period-sheet
      v-model="periodSheet"
      :from="from"
      :to="to"
      :presets="presets"
      @apply="applyPeriod"
    ></report-mobile-period-sheet>

    <report-mobile-filter-sheet
      v-model="filterSheet"
      :from="from"
      :to="to"
      :presets="presets"
      :shifts="shifts"
      :show-shifts="Boolean(currentScreen && currentScreen.shifts)"
      :show-companies="Boolean(currentScreen && currentScreen.companies)"
      :company-mode="singleCompany ? 'single' : 'multi'"
      :companies="companyList"
      :selected-company-ids="screenCompanyIds"
      :views="views"
      :selected-view="selectedView"
      @open-period="openPeriodFromFilters"
      @apply="applyFilters"
    ></report-mobile-filter-sheet>

    <ui-sheet v-model="actionsSheet" :label="$t('report_mobile.more_options')">
      <ul class="rm-list rm-actions">
        <li>
          <button type="button" class="rm-actions__row" @click="runAction('print')">
            <v-icon size="22" color="#1c1917">mdi-printer-outline</v-icon>
            {{ $t("report_mobile.print_pdf") }}
          </button>
        </li>
        <li v-if="currentScreen && currentScreen.printNames">
          <label class="rm-actions__row rm-actions__row--check">
            <input v-model="showCompanyNames" type="checkbox" />
            {{ $t("show_company_names_print") }}
          </label>
        </li>
        <li v-if="!(currentScreen && currentScreen.noExcel)">
          <button type="button" class="rm-actions__row" @click="runAction('excel')">
            <v-icon size="22" color="#15803d">mdi-microsoft-excel</v-icon>
            {{ $t("report_mobile.download_excel") }}
          </button>
        </li>
        <li>
          <button type="button" class="rm-actions__row" @click="runAction('reload')">
            <v-icon size="22" color="#1c1917">mdi-refresh</v-icon>
            {{ $t("report_mobile.refresh") }}
          </button>
        </li>
      </ul>
    </ui-sheet>
  </div>
</template>

<script>
import ReportMobileMeals from "./screens/ReportMobileMeals.vue";
import ReportMobileEmployees from "./screens/ReportMobileEmployees.vue";
import ReportMobileEmployee from "./screens/ReportMobileEmployee.vue";
import ReportMobilePackages from "./screens/ReportMobilePackages.vue";
import ReportMobileWorkerOrders from "./screens/ReportMobileWorkerOrders.vue";
import ReportMobileTaken from "./screens/ReportMobileTaken.vue";
import ReportMobileOrdered from "./screens/ReportMobileOrdered.vue";
import ReportMobileTakenMeals from "./screens/ReportMobileTakenMeals.vue";
import ReportMobileReserved from "./screens/ReportMobileReserved.vue";
import ReportMobileGroceries from "./screens/ReportMobileGroceries.vue";
import ReportMobileFoods from "./screens/ReportMobileFoods.vue";
import ReportMobileNonRegistered from "./screens/ReportMobileNonRegistered.vue";
import ReportMobileRestaurants from "./screens/ReportMobileRestaurants.vue";
import ReportMobileAllSummary from "./screens/ReportMobileAllSummary.vue";
import ReportMobileAllPacking from "./screens/ReportMobileAllPacking.vue";
import GroupMobileMeals from "./screens/group/GroupMobileMeals.vue";
import GroupMobileGrouped from "./screens/group/GroupMobileGrouped.vue";
import GroupMobileUsers from "./screens/group/GroupMobileUsers.vue";
import GroupMobileOrdered from "./screens/group/GroupMobileOrdered.vue";
import GroupMobileOrderers from "./screens/group/GroupMobileOrderers.vue";
import GroupMobileReserved from "./screens/group/GroupMobileReserved.vue";
import GroupMobileTaken from "./screens/group/GroupMobileTaken.vue";
import GroupMobileRestaurants from "./screens/group/GroupMobileRestaurants.vue";
import { buildMoreScreens, CREDIT_USER_SCREEN, MORE_MENU } from "./moreScreens";
import ReportMobilePeriodSheet from "./ReportMobilePeriodSheet.vue";
import ReportMobileFilterSheet from "./ReportMobileFilterSheet.vue";
import "./reportMobileShell.scss";
import "./screens/group/reportMobileGroup.scss";
import hasPlural from "../shared/hasPlural";
import { buildPeriodPresets, findPresetKey, formatRange, formatShortDate } from "../shared/periods";

const ALL_SHIFTS = [1, 2, 3, 4];
const SCREEN_QUERY_KEY = "r";
const EMPLOYEE_SCREEN = "employee";
const GROUP_MEALS_VIEW = 2;
const MORE_RESTAURANTS_VIEW = 3;
const ALL_COMPANIES_VIEW = 4;

export default {
  name: "ReportMobile",
  mixins: [hasPlural],
  components: {
    ReportMobilePeriodSheet,
    ReportMobileFilterSheet,
  },
  props: {
    /** [from, to] u formatu YYYY-MM-DD */
    value: {
      type: Array,
      required: true,
    },
    selectedView: {
      type: Number,
      default: 1,
    },
    views: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    shifts: [...ALL_SHIFTS],
    periodSheet: false,
    filterSheet: false,
    actionsSheet: false,
    searchOpen: false,
    reloadKey: 0,
    pushedScreens: 0,
    pendingSearch: "",
    /** Firme tipa grupa (prikaz Jela iz grupa) su učitane i imaju početni izbor. */
    foodReady: false,
    /** Firme "više restorana" (prikaz Firme koje hrani restoran) su učitane i imaju početni izbor. */
    multiReady: false,
    /** Sve firme restorana imaju izbor; "false" samo dok se izbor preuzet iz prikaza 3 ne vrati na početni. */
    mealReady: true,
  }),
  computed: {
    from() {
      return this.value[0];
    },
    to() {
      return this.value[1] || this.value[0];
    },
    presets() {
      return buildPeriodPresets();
    },
    quickPresets() {
      return this.presets.filter((preset) => preset.key !== "last_month");
    },
    activePresetKey() {
      return findPresetKey(this.presets, this.from, this.to);
    },
    activeQuickPreset() {
      return this.quickPresets.some((preset) => preset.key === this.activePresetKey);
    },
    periodShort() {
      if (this.activePresetKey) {
        return this.$t(`report_mobile.presets.${this.activePresetKey}`);
      }
      return formatRange(this.from, this.to);
    },
    periodLong() {
      if (this.from !== this.to) {
        return formatRange(this.from, this.to);
      }
      const weekday = this.localeDate(this.from, { weekday: "long" });
      const capitalized = weekday.charAt(0).toUpperCase() + weekday.slice(1);
      return `${capitalized}, ${formatShortDate(this.from, true)}`;
    },
    restaurantName() {
      return this.toLatin(this.$store.state.restaurant?.name || "");
    },
    screens() {
      return {
        groceries: {
          title: this.$t("groceri"),
          subtitle: this.$t("report_mobile.hints.groceries"),
          icon: "mdi-leaf",
          component: ReportMobileGroceries,
          headerSearch: true,
        },
        foods: {
          title: this.$t("meals_main"),
          subtitle: this.$t("report_mobile.hints.foods"),
          icon: "mdi-pot-steam-outline",
          component: ReportMobileFoods,
          headerSearch: true,
        },
        meals: {
          title: this.$t("meals"),
          subtitle: this.$t("report_mobile.hints.meals"),
          icon: "mdi-bowl-mix-outline",
          component: ReportMobileMeals,
          companies: true,
          headerSearch: true,
        },
        workerOrders: {
          title: this.$t("report_mobile.worker_orders"),
          subtitle: this.$t("report_mobile.hints.worker_orders"),
          icon: "mdi-table",
          component: ReportMobileWorkerOrders,
          companies: true,
          shifts: true,
        },
        employees: {
          title: this.$t("employees"),
          subtitle: this.$t("report_mobile.hints.employees"),
          icon: "mdi-account-outline",
          component: ReportMobileEmployees,
          companies: "single",
          headerSearch: true,
        },
        /** Nije u meniju: otvara se iz liste radnika (store: report/selectedEmployee). */
        employee: {
          title: this.employeeContext?.fullName || this.$t("employees"),
          subtitleText: this.employeeSubtitle,
          component: ReportMobileEmployee,
          header: "worker",
          noExcel: true,
        },
        reserved: {
          title: this.$t("report_mobile.reserves"),
          subtitle: this.$t("r_o"),
          icon: "mdi-archive-outline",
          component: ReportMobileReserved,
          companies: true,
          headerSearch: true,
          header: "summary",
        },
        packages: {
          title: this.$t("pak"),
          subtitle: this.$t("report_mobile.hints.packages"),
          icon: "mdi-shopping-outline",
          component: ReportMobilePackages,
          companies: true,
          shifts: true,
          headerSearch: true,
          header: "summary",
        },
        taken: {
          title: this.$t("taken"),
          subtitle: this.$t("report_mobile.hints.taken"),
          icon: "mdi-truck-outline",
          component: ReportMobileTaken,
          shifts: true,
          headerSearch: true,
          header: "summary",
        },
        takenMeals: {
          title: this.$t("t_o"),
          subtitle: this.$t("report_mobile.hints.taken_meals"),
          icon: "mdi-check-circle-outline",
          component: ReportMobileTakenMeals,
          companies: true,
          shifts: true,
          headerSearch: true,
          header: "summary",
        },
        ordered: {
          title: this.$t("warehouse.ordered"),
          subtitle: this.$t("report_mobile.hints.ordered"),
          icon: "mdi-grid",
          component: ReportMobileOrdered,
          companies: "single",
          shifts: true,
          headerSearch: true,
        },
        nonRegistered: {
          title: this.$t("un"),
          subtitle: this.$t("report_mobile.hints.non_registered"),
          icon: "mdi-account-question-outline",
          component: ReportMobileNonRegistered,
          headerSearch: true,
        },
        restaurants: {
          title: this.$t("restaurants"),
          subtitle: this.$t("report_mobile.hints.restaurants"),
          icon: "mdi-storefront-outline",
          component: ReportMobileRestaurants,
          headerSearch: true,
        },
        groupMeals: {
          title: this.$t("meals_main"),
          subtitle: this.$t("report_group.hints.meals"),
          icon: "mdi-food-drumstick",
          component: GroupMobileMeals,
          companies: true,
          companySource: "food",
          shifts: true,
          headerSearch: true,
          printNames: true,
        },
        groupGrouped: {
          title: this.$t("groups_meal"),
          subtitle: this.$t("report_group.hints.grouped"),
          icon: "mdi-view-grid-plus",
          component: GroupMobileGrouped,
          companies: true,
          companySource: "food",
          shifts: true,
          printNames: true,
        },
        groupUsers: {
          title: this.$t("users"),
          subtitle: this.$t("report_group.hints.users"),
          icon: "mdi-account-multiple",
          component: GroupMobileUsers,
          companies: "single",
          companySource: "food",
          headerSearch: true,
        },
        groupOrdered: {
          title: this.$t("ordered_1"),
          subtitle: this.$t("report_group.hints.ordered"),
          icon: "mdi-grid",
          component: GroupMobileOrdered,
          companies: "single",
          companySource: "food",
          headerSearch: true,
        },
        groupOrderers: {
          title: this.$t("orderers"),
          subtitle: this.$t("report_group.hints.orderers"),
          icon: "mdi-format-list-bulleted-type",
          component: GroupMobileOrderers,
          companies: "single",
          companySource: "food",
          headerSearch: true,
        },
        groupReserved: {
          title: this.$t("r_o"),
          subtitle: this.$t("report_group.hints.reserved"),
          icon: "mdi-food-variant",
          component: GroupMobileReserved,
          companies: true,
          headerSearch: true,
        },
        groupTaken: {
          title: this.$t("ordered_taken_meals"),
          subtitle: this.$t("report_group.hints.taken"),
          icon: "mdi-truck-delivery",
          component: GroupMobileTaken,
          shifts: true,
          headerSearch: true,
        },
        groupRestaurants: {
          title: this.$t("restaurants"),
          subtitle: this.$t("report_group.hints.restaurants"),
          icon: "mdi-storefront-outline",
          component: GroupMobileRestaurants,
          headerSearch: true,
        },
        ...buildMoreScreens(this),
        allMeals: {
          title: this.$t("meals"),
          subtitle: this.$t("report_mobile.hints.all_meals"),
          icon: "mdi-bowl-mix-outline",
          component: ReportMobileAllSummary,
          props: { mode: "meals" },
          headerSearch: true,
        },
        allCompanies: {
          title: this.$t("companies"),
          subtitle: this.$t("report_mobile.hints.all_companies"),
          icon: "mdi-domain",
          component: ReportMobileAllSummary,
          props: { mode: "companies" },
          headerSearch: true,
        },
        allPacking: {
          title: this.$t("pak"),
          subtitle: this.$t("report_mobile.hints.all_packing"),
          icon: "mdi-shopping-outline",
          component: ReportMobileAllPacking,
          shifts: true,
          headerSearch: true,
          header: "summary",
          bindSelectedShifts: true,
        },
      };
    },
    currentViewName() {
      return this.views.find((view) => view.id === this.selectedView)?.name || "";
    },
    /** Prikaz "Sve firme – zbirni izvještaj" ima svoje ekrane. */
    isAllCompaniesView() {
      return this.selectedView === ALL_COMPANIES_VIEW;
    },
    /** Prikaz "Jela iz grupa" ima svoje ekrane i firme tipa grupa. */
    isGroupView() {
      return this.selectedView === GROUP_MEALS_VIEW;
    },
    /** Prikaz "Firme koje hrani restoran" ima svoje ekrane i firme sa ugovorom "više restorana". */
    isMoreView() {
      return this.selectedView === MORE_RESTAURANTS_VIEW;
    },
    screenProps() {
      const screen = this.currentScreen || {};
      return {
        ...(screen.props || {}),
        ...(screen.bindSelectedShifts ? { selectedShifts: this.shifts } : {}),
      };
    },
    menuGroups() {
      const group = (key, title, keys) => ({
        key,
        title,
        items: keys.map((itemKey) => ({ key: itemKey, ...this.screens[itemKey] })),
      });
      if (this.isAllCompaniesView) {
        return [
          group("all", this.$t("all_companies_summary_report"), [
            "allMeals",
            "allCompanies",
            "allPacking",
          ]),
        ];
      }
      if (this.isMoreView) {
        return [
          group("kitchen", this.$t("report_mobile.groups.kitchen"), MORE_MENU.kitchen),
          group("orders", this.$t("report_mobile.groups.orders"), MORE_MENU.orders),
          group("handout", this.$t("report_mobile.groups.handout"), MORE_MENU.handout),
        ];
      }
      if (this.isGroupView) {
        return [
          group("kitchen", this.$t("report_mobile.groups.kitchen"), ["groupMeals", "groupGrouped"]),
          group("orders", this.$t("report_mobile.groups.orders"), [
            "groupUsers",
            "groupOrdered",
            "groupOrderers",
            "groupReserved",
          ]),
          group("handout", this.$t("report_mobile.groups.handout"), ["groupTaken"]),
          group("other", this.$t("report_mobile.groups.other"), ["groupRestaurants"]),
        ];
      }
      return [
        group("kitchen", this.$t("report_mobile.groups.kitchen"), [
          "groceries",
          "foods",
          "meals",
        ]),
        group("orders", this.$t("report_mobile.groups.orders"), [
          "workerOrders",
          "employees",
          "reserved",
        ]),
        group("handout", this.$t("report_mobile.groups.handout"), [
          "packages",
          "taken",
          "takenMeals",
        ]),
        group("other", this.$t("report_mobile.groups.other"), [
          "ordered",
          "nonRegistered",
          "restaurants",
        ]),
      ];
    },
    currentScreenKey() {
      const key = this.$route.query[SCREEN_QUERY_KEY];
      if (key === EMPLOYEE_SCREEN && this.employeeContext && !this.employeeContext.credit) {
        return key;
      }
      if (key === CREDIT_USER_SCREEN && this.isMoreView && this.employeeContext?.credit) {
        return key;
      }
      // Mesečni pregled radnika (i kreditnog korisnika) nema smisla bez izabranog radnika (npr. posle osvežavanja).
      let wanted = key;
      if (key === EMPLOYEE_SCREEN) {
        wanted = this.isGroupView ? "groupUsers" : this.isMoreView ? "moreEmployees" : "employees";
      } else if (key === CREDIT_USER_SCREEN) {
        wanted = "moreCredit";
      }
      const allowed = this.menuGroups.some((group) =>
        group.items.some((item) => item.key === wanted)
      );
      return allowed ? wanted : null;
    },
    currentScreen() {
      return this.currentScreenKey ? this.screens[this.currentScreenKey] : null;
    },
    mealCompanies() {
      return this.$store.state.report.mealCompanies || [];
    },
    /**
     * Spisak firmi koji ekran koristi: "food" su firme tipa grupa (Jela iz grupa), "multi" firme sa ugovorom "više
     * restorana" (ceo prikaz Firme koje hrani restoran), inače sve firme restorana. "multi" i "meal" dele izbor
     * firmi u store-u, jer ga stari tabovi tako čitaju.
     */
    companySource() {
      if (this.isMoreView) {
        return "multi";
      }
      return this.currentScreen?.companySource === "food" ? "food" : "meal";
    },
    multiCompanies() {
      return (this.$store.state.report.restaurantMultiCompanies || []).map((company) => ({
        ...company,
        name: this.toLatin(company.name || company.company || ""),
      }));
    },
    companyList() {
      if (this.companySource === "multi") {
        return this.multiCompanies;
      }
      return this.companySource === "food" ? this.$store.state.report.foodCompanies || [] : this.mealCompanies;
    },
    companiesReady() {
      if (this.companySource === "multi") {
        return this.multiReady;
      }
      return this.companySource === "food" ? this.foodReady : this.mealReady;
    },
    selectedCompanies() {
      const report = this.$store.state.report;
      return (this.companySource === "food" ? report.selectedFoodCompanies : report.selectedMealCompanies) || [];
    },
    selectedCompanyIds() {
      return this.selectedCompanies.map((company) => company.id);
    },
    /** "Radnici" čitaju jednu firmu (selectedMealCompany); ostali ekrani više njih. */
    singleCompany() {
      return this.currentScreen?.companies === "single";
    },
    selectedMealCompany() {
      const report = this.$store.state.report;
      return this.companySource === "food" ? report.selectedCompany : report.selectedMealCompany;
    },
    screenCompanyIds() {
      if (this.singleCompany) {
        return this.selectedMealCompany ? [this.selectedMealCompany.id] : [];
      }
      return this.selectedCompanyIds;
    },
    companyChipAccent() {
      return this.singleCompany ? Boolean(this.selectedMealCompany) : !this.allCompaniesSelected;
    },
    companiesChipLabel() {
      if (this.singleCompany) {
        return this.selectedMealCompany
          ? this.toLatin(this.selectedMealCompany.name)
          : this.$t("select_company");
      }
      return this.companiesLabel;
    },
    employeeContext() {
      return this.$store.state.report.selectedEmployee;
    },
    employeeSubtitle() {
      const employee = this.employeeContext;
      return employee ? [`ID ${employee.id}`, employee.companyName].filter(Boolean).join(" · ") : "";
    },
    /** Podnaslov zaglavlja: datum, sažetak filtera ("Danas · smene 1–4 · 6 firmi") ili radnik. */
    headerSubtitle() {
      const screen = this.currentScreen;
      if (!screen) {
        return "";
      }
      if (screen.header === "summary") {
        return this.summaryLine;
      }
      return screen.subtitleText || this.periodLong;
    },
    summaryLine() {
      const parts = [this.periodShort];
      if (this.currentScreen?.shifts) {
        parts.push(this.shiftsInline);
      }
      parts.push(
        this.currentScreen?.companies
          ? this.companiesInline
          : this.$t("report_mobile.all_companies_inline")
      );
      return parts.join(" · ");
    },
    shiftsInline() {
      const list = [...this.shifts].sort((a, b) => a - b);
      if (list.length === 1) {
        return this.$t("report_mobile.shift_inline", { n: list[0] });
      }
      const contiguous = list.length > 2 && list[list.length - 1] - list[0] + 1 === list.length;
      return this.$t("report_mobile.shifts_inline", {
        list: contiguous ? `${list[0]}–${list[list.length - 1]}` : list.join(", "),
      });
    },
    companiesInline() {
      if (this.selectedCompanies.length === 1) {
        return this.toLatin(this.selectedCompanies[0].name);
      }
      return this.pluralT("report_mobile.companies_n", this.selectedCompanies.length);
    },
    allCompaniesSelected() {
      return (
        this.companyList.length > 0 &&
        this.selectedCompanies.length === this.companyList.length
      );
    },
    companiesLabel() {
      if (this.selectedCompanies.length === 1) {
        return this.toLatin(this.selectedCompanies[0].name);
      }
      if (this.allCompaniesSelected) {
        return this.$t("report_mobile.all_companies_n", { n: this.companyList.length });
      }
      return this.pluralT("report_mobile.companies_n", this.selectedCompanies.length);
    },
    allShiftsSelected() {
      return this.shifts.length === ALL_SHIFTS.length;
    },
    shiftsLabel() {
      if (this.allShiftsSelected) {
        return this.$t("report_mobile.all_shifts");
      }
      if (this.shifts.length === 1) {
        return this.$t("report_mobile.shift_n", { n: this.shifts[0] });
      }
      return this.$t("report_mobile.shifts_list", { list: this.shifts.join(", ") });
    },
    /** "Prikaži nazive firmi prilikom štampe" (jela iz grupa): isti izbor koji čitaju stari tabovi. */
    showCompanyNames: {
      get() {
        return this.$store.state.report.filters.showCompanyNames;
      },
      set(value) {
        this.$store.commit("report/SET_FILTER", { key: "showCompanyNames", value });
      },
    },
    search: {
      get() {
        return this.$store.state.report.filters.search;
      },
      set(value) {
        this.$store.commit("report/SET_FILTER", { key: "search", value });
      },
    },
  },
  watch: {
    companySource(source) {
      if (source === "food") {
        this.ensureCompanies("food");
        return;
      }
      // meal i multi dele izbor: kad ga je poslednji postavio drugi izvor, ekran čeka da se vrati na početni
      if (this.selectionOwner && this.selectionOwner !== source) {
        this[source === "multi" ? "multiReady" : "mealReady"] = false;
      }
      this.ensureCompanies(source);
    },
    currentScreenKey(newKey, oldKey) {
      if (newKey === oldKey) {
        return;
      }
      this.search = this.pendingSearch;
      this.pendingSearch = "";
      this.searchOpen = Boolean(this.search) && Boolean(this.currentScreen?.headerSearch);
      window.scrollTo(0, 0);
    },
  },
  created() {
    // Store pamti period iz prethodne posete; ekran uvek kreće od perioda koji prikazuje.
    this.$store.commit("report/SET_DATE_RANGE", { from: this.from, to: this.to });
    this.selectionOwner = null; // izvor (meal/multi) koji je poslednji postavio izbor firmi; ne treba da bude reaktivan
    if (this.companySource === "multi") {
      this.ensureCompanies("multi");
      return;
    }
    this.ensureCompanies("meal");
    if (this.companySource === "food") {
      this.ensureCompanies("food");
    }
  },
  beforeDestroy() {
    this.search = "";
  },
  methods: {
    async ensureCompanies(source = "meal") {
      if (source === "food") {
        await this.loadFoodCompanies();
        const report = this.$store.state.report;
        const list = report.foodCompanies || [];
        if (!report.selectedFoodCompanies.length && list.length) {
          this.$store.commit("report/SET_SELECTED_FOOD_COMPANIES", [...list]);
        }
        // izveštaji za jednu firmu odmah dobiju prvu firmu, kao u starom izboru firme
        if (!report.selectedCompany && list.length) {
          this.$store.commit("report/SET_SELECTED_COMPANY", list[0]);
        }
        this.foodReady = true;
        return;
      }
      if (source === "multi") {
        await this.ensureMultiCompanies();
        return;
      }
      const ownerChanged = Boolean(this.selectionOwner) && this.selectionOwner !== "meal";
      if (!this.mealCompanies.length) {
        await this.$store.dispatch("report/fetchMealCompanies");
      }
      if (ownerChanged) {
        this.resetSharedSelection();
      }
      if (!this.$store.state.report.selectedMealCompanies.length && this.mealCompanies.length) {
        this.$store.commit("report/SET_SELECTED_MEAL_COMPANIES", [...this.mealCompanies]);
      }
      this.selectionOwner = "meal";
      this.mealReady = true;
    },
    /**
     * Firme "više restorana": učitava ih i postavlja početni izbor (prva firma, kao u starom izgledu). Izbor koji nije
     * u spisku (ostao iz prikaza 1) se odbacuje.
     */
    async ensureMultiCompanies() {
      const ownerChanged = Boolean(this.selectionOwner) && this.selectionOwner !== "multi";
      if (!this.multiCompanies.length) {
        await this.$store.dispatch("report/fetchRestaurantMultiCompanies");
      }
      const list = this.multiCompanies;
      if (ownerChanged) {
        this.resetSharedSelection();
      }
      const report = this.$store.state.report;
      const valid = report.selectedMealCompanies.filter((company) => list.some((item) => item.id === company.id));
      if (valid.length !== report.selectedMealCompanies.length) {
        this.$store.commit("report/SET_SELECTED_MEAL_COMPANIES", valid);
      }
      if (!valid.length && list.length) {
        this.$store.commit("report/SET_SELECTED_MEAL_COMPANIES", [list[0]]);
      }
      const single = report.selectedMealCompany;
      if (single && !list.some((item) => item.id === single.id)) {
        this.$store.commit("report/SET_SELECTED_MEAL_COMPANY", null);
      }
      // izveštaji za jednu firmu odmah dobiju prvu firmu, kao u starom izboru firme
      if (!this.$store.state.report.selectedMealCompany && list.length) {
        this.$store.commit("report/SET_SELECTED_MEAL_COMPANY", list[0]);
      }
      this.selectionOwner = "multi";
      this.multiReady = true;
    },
    /** Izbor firmi koji dele prikazi 1 i 3 kreće ispočetka kad se prikaz promeni. */
    resetSharedSelection() {
      this.$store.commit("report/SET_SELECTED_MEAL_COMPANIES", []);
      this.$store.commit("report/SET_SELECTED_MEAL_COMPANY", null);
    },
    /** Firme tipa grupa: stranica ih već traži pri promeni prikaza, pa se čeka taj poziv da se ne duplira. */
    async loadFoodCompanies() {
      if (this.$store.state.report.loading.fetchFoodCompanies) {
        await new Promise((resolve) => {
          const stop = this.$watch(
            () => this.$store.state.report.loading.fetchFoodCompanies,
            (loading) => {
              if (!loading) {
                stop();
                resolve();
              }
            }
          );
        });
      }
      const restaurantId = this.$store.state.restaurant?.id;
      if (!this.$store.state.report.foodCompanies.length && restaurantId) {
        await this.$store.dispatch("report/fetchFoodCompanies", {
          date_from: this.from,
          date_to: this.to,
          restaurant_id: restaurantId,
        });
      }
    },
    presetChipLabel(preset) {
      const label = this.$t(`report_mobile.presets.${preset.key}`);
      const isSingleDay = preset.from === preset.to;
      if (this.activePresetKey === preset.key && isSingleDay) {
        return `${label} · ${formatShortDate(preset.from)}`;
      }
      return label;
    },
    applyPeriod({ from, to }) {
      this.$emit("input", [from, to]);
    },
    openPeriodFromFilters() {
      this.filterSheet = false;
      this.periodSheet = true;
    },
    applyFilters({ from, to, shifts, companyIds, view }) {
      if (from !== this.from || to !== this.to) {
        this.applyPeriod({ from, to });
      }
      this.shifts = shifts;
      const food = this.companySource === "food";
      if (this.singleCompany) {
        const company = this.companyList.find((item) => companyIds.includes(item.id)) || null;
        if (company?.id !== this.selectedMealCompany?.id) {
          this.$store.commit(food ? "report/SET_SELECTED_COMPANY" : "report/SET_SELECTED_MEAL_COMPANY", company);
        }
      } else if (companyIds.join(",") !== this.selectedCompanyIds.join(",")) {
        this.$store.commit(
          food ? "report/SET_SELECTED_FOOD_COMPANIES" : "report/SET_SELECTED_MEAL_COMPANIES",
          this.companyList.filter((company) => companyIds.includes(company.id))
        );
      }
      if (view !== this.selectedView) {
        this.$emit("update:selectedView", view);
      }
    },
    openScreen(key, { search = "" } = {}) {
      if (key === this.currentScreenKey) {
        this.search = search;
        return;
      }
      this.pendingSearch = search;
      this.pushedScreens += 1;
      this.$router
        .push({ query: { ...this.$route.query, [SCREEN_QUERY_KEY]: key } })
        .catch(() => {});
    },
    closeScreen() {
      // Ekran otvoren sa ove stranice → nazad kroz istoriju (radi i hardverski "nazad");
      // direktan link (?r=...) → zameni URL, da ne izađemo sa /izvestaj.
      if (this.pushedScreens > 0) {
        this.pushedScreens -= 1;
        this.$router.back();
        return;
      }
      const query = { ...this.$route.query };
      delete query[SCREEN_QUERY_KEY];
      this.$router.replace({ query }).catch(() => {});
    },
    toggleSearch() {
      this.searchOpen = !this.searchOpen;
      if (this.searchOpen) {
        this.$nextTick(() => this.$refs.searchInput?.focus());
      } else {
        this.search = "";
      }
    },
    runAction(action) {
      this.actionsSheet = false;
      if (action === "print") {
        this.$store.commit("SET_PRINTING");
      } else if (action === "excel") {
        this.$store.commit("SET_EXCEL");
      } else if (action === "reload") {
        this.reloadKey += 1;
      }
    },
  },
};
</script>
